import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Info, ChevronRight, Users, BookOpen, Star, User, List, DollarSign, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

interface StepItem {
  step: number;
  title: string;
  description: string;
  icon: React.ReactNode;
}

export const HowItWorksSection: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'jogador' | 'mentor'>('jogador');

  const stepsJogador: StepItem[] = [
    {
      step: 1,
      title: "Escolha seu Mentor",
      description: "Navegue pelos perfis de mentores e encontre o especialista ideal para o seu jogo favorito.",
      icon: <Users className="h-7 w-7" />
    },
    {
      step: 2,
      title: "Agende sua Mentoria",
      description: "Escolha o formato de mentoria que prefere e selecione um horário disponível na agenda do mentor.",
      icon: <BookOpen className="h-7 w-7" />
    },
    {
      step: 3,
      title: "Evolua seu Gameplay",
      description: "Participe da sessão, aprenda com profissionais e eleve seu nível de jogo com feedback personalizado.",
      icon: <Star className="h-7 w-7" />
    }
  ];

  const stepsMentor: StepItem[] = [
    {
      step: 1,
      title: "Crie seu Perfil",
      description: "Registre-se facilmente e construa seu perfil profissional, incluindo suas redes sociais e agendamento de horários.",
      icon: <User className="h-7 w-7" />
    },
    {
      step: 2,
      title: "Divulgue suas Mentorias",
      description: "Cadastre suas opções de mentoria, definindo jogo, duração, modalidade e preço para atrair jogadores.",
      icon: <List className="h-7 w-7" />
    },
    {
      step: 3,
      title: "Gerencie e Ganhe",
      description: "Receba notificações sobre sessões agendadas, conduza mentorias e ganhe dinheiro ajudando outros jogadores.",
      icon: <DollarSign className="h-7 w-7" />
    }
  ];

  const currentSteps = activeTab === 'jogador' ? stepsJogador : stepsMentor;

  return (
    <section id="como-funciona-section" className="py-16 md:py-24 px-4 bg-[#0c1117] relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-center opacity-[0.07]" />
      <div className="pointer-events-none absolute -top-24 -left-24 w-72 h-72 bg-[#00B6F0]/20 rounded-full blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 w-72 h-72 bg-[#00B6F0]/10 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00B6F0]/10 border border-[#00B6F0]/30 mb-4">
            <Sparkles className="h-3.5 w-3.5 text-[#00B6F0]" />
            <span className="text-xs font-semibold text-[#00B6F0] tracking-wider uppercase">Passo a passo</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight">
            Como <span className="bg-gradient-to-r from-[#00B6F0] to-[#7BD7FF] bg-clip-text text-transparent">Funciona</span>
          </h2>
          <p className="text-gray-400 mt-3 max-w-xl mx-auto text-sm md:text-base">
            Em apenas 3 passos você começa sua jornada na Arena Pro Player.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-12 md:mb-16">
          <div className="relative flex bg-[#181D24] rounded-full p-1 border border-[#223342] shadow-lg shadow-black/40">
            {(['jogador', 'mentor'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`relative z-10 px-5 sm:px-8 py-2.5 rounded-full text-sm sm:text-base font-semibold transition-colors duration-300 ${
                  activeTab === tab ? 'text-black' : 'text-gray-400 hover:text-white'
                }`}
              >
                {activeTab === tab && (
                  <motion.div
                    layoutId="howTabPill"
                    className="absolute inset-0 bg-gradient-to-r from-[#00B6F0] to-[#33C6FF] rounded-full shadow-[0_0_20px_rgba(0,182,240,0.5)]"
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
                <span className="relative">{tab === 'jogador' ? 'Como Jogador' : 'Como Mentor'}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Steps */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4 }}
            className="relative"
          >
            {/* Desktop connecting line */}
            <div className="hidden md:block absolute top-10 left-[16.66%] right-[16.66%] h-px bg-gradient-to-r from-transparent via-[#00B6F0]/40 to-transparent" />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 relative">
              {currentSteps.map((item, index) => (
                <motion.div
                  key={`${activeTab}-${index}`}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.12 + 0.15, duration: 0.5 }}
                  className="relative group"
                >
                  {/* Mobile vertical connector */}
                  {index < currentSteps.length - 1 && (
                    <div className="md:hidden absolute left-9 top-20 bottom-[-1.5rem] w-px bg-gradient-to-b from-[#00B6F0]/40 to-transparent" />
                  )}

                  <div className="relative bg-gradient-to-br from-[#181D24] to-[#10151c] border border-[#223342] rounded-2xl p-6 md:p-7 md:pt-16 transition-all duration-300 group-hover:border-[#00B6F0]/50 group-hover:-translate-y-1 group-hover:shadow-[0_10px_40px_-10px_rgba(0,182,240,0.4)] flex md:block gap-5">
                    {/* Giant step number watermark (desktop) */}
                    <span
                      aria-hidden
                      className="hidden md:block absolute top-3 right-5 text-[6rem] leading-none font-black text-white/[0.04] select-none"
                    >
                      0{item.step}
                    </span>

                    {/* Icon badge */}
                    <div className="relative shrink-0 md:absolute md:-top-7 md:left-7">
                      <div className="absolute inset-0 bg-[#00B6F0] blur-xl opacity-30 group-hover:opacity-60 transition-opacity rounded-2xl" />
                      <div className="relative w-[68px] h-[68px] rounded-2xl bg-gradient-to-br from-[#00B6F0] to-[#0088B8] flex items-center justify-center text-black shadow-lg shadow-[#00B6F0]/30">
                        {item.icon}
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] font-bold tracking-[0.2em] text-[#00B6F0] uppercase">Passo {item.step}</span>
                        <span className="h-px flex-1 bg-[#223342]" />
                      </div>
                      <h3 className="text-white text-lg md:text-xl font-bold mb-2 leading-tight">{item.title}</h3>
                      <p className="text-gray-400 text-sm md:text-[15px] leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="mt-12 md:mt-16 text-center">
          <Button
            onClick={() => navigate("/como-funciona")}
            size="lg"
            className="bg-transparent border border-[#00B6F0]/50 text-[#00B6F0] hover:bg-[#00B6F0] hover:text-black hover:border-[#00B6F0] transition-all rounded-full px-8 group"
          >
            Saiba Mais
            <ChevronRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>
      </div>
    </section>
  );
};
